// What a moved date, or a new link, does to the work that depends on it.
//
// PUSH LATER, NEVER PULL EARLIER. When a work item's dates change (a bar is
// dragged) or a dependency is drawn between two bars, every successor that
// would now start (or finish) too early is moved LATER by exactly enough,
// and so is whatever depends on THAT, and so on down the chain. Nothing is
// ever moved earlier, and nothing upstream of the change is touched: a plan
// that already satisfies its links is left exactly as it is. That is the
// whole promise, and it is why a grid can do this on a live site without
// rewriting dates the change had nothing to do with.
//
// The arithmetic is the folders Gantt's (ganttDependencyHelpers.js): the
// predecessor's anchor plus the rule's lag plus one for a cross-anchor kind,
// counted in WORKING days, is the earliest the successor's anchor may be.
//   FS  succ.start >= pred.end   + lag + 1      FF  succ.end >= pred.end   + lag
//   SS  succ.start >= pred.start + lag          SF  succ.end >= pred.start + lag + 1
// A successor is moved whole — both dates by the same number of calendar
// days — so its duration is what it was.
//
// Pure: no React, no fetch. Dates are 'YYYY-MM-DD' strings, done in UTC so a
// daylight-saving night cannot shift one by a day.

const DAY_MS = 86400000;
const toMs = (iso) => Date.parse(`${iso}T00:00:00Z`);
export const plusDays = (iso, n) => new Date(toMs(iso) + n * DAY_MS).toISOString().slice(0, 10);
export const daysBetween = (a, b) => Math.round((toMs(b) - toMs(a)) / DAY_MS);
const isWeekend = (iso) => { const d = new Date(toMs(iso)).getUTCDay(); return d === 0 || d === 6; };

// n working days on from `iso` (Saturdays and Sundays do not count). n = 0
// is the day itself, whatever day that is — the folders Gantt's rule.
export function addWorkingDays(iso, n) {
  if (!iso || !n) return iso;
  const step = n > 0 ? 1 : -1;
  let d = iso;
  let left = Math.abs(n);
  while (left > 0) {
    d = plusDays(d, step);
    if (!isWeekend(d)) left -= 1;
  }
  return d;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const ok = (v) => typeof v === 'string' && DATE.test(v);
const predAnchor = (type, it) => (type === 'FS' || type === 'FF' ? it.end : it.start);
const succSide = (type) => (type === 'FS' || type === 'SS' ? 'start' : 'end');
const gapOf = (type) => (type === 'FS' || type === 'SF' ? 1 : 0);

// The earliest the successor's anchor may be, given the predecessor.
export function earliestFor(edge, pred) {
  const a = predAnchor(edge.type, pred);
  if (!ok(a)) return null;
  return addWorkingDays(a, (Number(edge.lagDays) || 0) + gapOf(edge.type));
}

// dates   Map<key, { start, end, frozen? }> — every work item the page holds,
//         with a changed item's NEW dates already in it. `frozen` (a Done
//         item) is never moved, and so nothing is pushed on through it.
// edges   [{ type, predKey, succKey, lagDays }] — buildDependencyEdges' shape,
//         including a link that has only just been made
// change  { moved: [keys whose dates just changed], links: [edges just made] }
//
// ONLY WHAT THE CHANGE REACHES IS ASKED. An edge is weighed when its
// predecessor is something that moved — or when it is one of the links just
// made. A successor that was already too early before the change, off some
// other predecessor, is not this change's business and is left as it was.
//
// Returns { updates: [{ key, start, end }], error } — updates name only the
// items that move; `error` is 'cycle' when the chain comes back round to the
// thing that changed (then nothing moves at all), else null.
export function pushLater(dates, edges, change) {
  const movedNow = new Set((change && change.moved) || []);
  const fresh = new Set((change && change.links) || []);
  // A link's predecessor is where its chain starts, so pushing it back round
  // is a cycle just as pushing the dragged item is.
  const origin = new Set(movedNow);
  fresh.forEach((e) => { if (e && e.predKey) origin.add(e.predKey); });

  const valid = (edges || []).filter((e) => e && e.predKey && e.succKey && e.predKey !== e.succKey && dates.has(e.predKey) && dates.has(e.succKey));
  // Breadth-first from the change, so the edges are weighed roughly in the
  // order the push travels — most chains settle in one round.
  const outOf = new Map();
  valid.forEach((e) => { if (!outOf.has(e.predKey)) outOf.set(e.predKey, []); outOf.get(e.predKey).push(e); });
  const order = [];
  const seenEdge = new Set();
  const seenKey = new Set();
  const q = [...origin];
  fresh.forEach((e) => { if (e && e.succKey) q.push(e.succKey); });
  while (q.length) {
    const k = q.shift();
    if (seenKey.has(k)) continue;
    seenKey.add(k);
    (outOf.get(k) || []).forEach((e) => { if (!seenEdge.has(e)) { seenEdge.add(e); order.push(e); q.push(e.succKey); } });
  }
  fresh.forEach((e) => { if (e && !seenEdge.has(e) && dates.has(e.predKey) && dates.has(e.succKey)) order.unshift(e); });

  const work = new Map();
  const get = (k) => {
    if (!work.has(k)) {
      const d = dates.get(k);
      work.set(k, { start: d.start || '', end: d.end || '', frozen: !!d.frozen });
    }
    return work.get(k);
  };
  const pushed = new Set();
  // Every edge at most once per round, and a round per item at the very most
  // (the longest chain there can be). Still changing after that is a loop.
  for (let round = 0; round <= seenKey.size + 1; round += 1) {
    let changed = false;
    for (const e of order) {
      if (!fresh.has(e) && !movedNow.has(e.predKey) && !pushed.has(e.predKey)) continue;
      const pred = get(e.predKey);
      const succ = get(e.succKey);
      if (succ.frozen) continue;
      const side = succSide(e.type);
      const need = earliestFor(e, pred);
      if (!need || !ok(succ[side])) continue;
      const by = daysBetween(succ[side], need);
      if (by <= 0) continue;
      // Back round to where the change started: moving anything would chase
      // its own tail, so nothing moves and the page says why.
      if (origin.has(e.succKey)) return { updates: [], error: 'cycle' };
      if (ok(succ.start)) succ.start = plusDays(succ.start, by);
      if (ok(succ.end)) succ.end = plusDays(succ.end, by);
      pushed.add(e.succKey);
      changed = true;
    }
    if (!changed) {
      const updates = [];
      pushed.forEach((k) => { const w = work.get(k); updates.push({ key: k, start: w.start || null, end: w.end || null }); });
      return { updates, error: null };
    }
  }
  return { updates: [], error: 'cycle' };
}
